import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAtom } from 'jotai';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListUl } from '@fortawesome/free-solid-svg-icons';
import { buyPack, fetchUserPacks } from '../../api/packInventory';
import { fetchWallet } from '../../api/wallet';
import { fetchStoreRotation } from '../../api/store';
import { fetchAllSeries } from '../../api/series';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { packCountAtom } from '../../atoms/packAtoms';
import { getCardImageSrc, handleCardImageError } from '../../hooks/useCardImage';
import PackBadge from '../../components/PackBadge';
import VariationRatesTooltip from '../../components/VariationRatesTooltip';
import SeriesCardsModal from '../../components/SeriesCardsModal';
import LoadingSpinner from '../../components/LoadingSpinner';

const formatCountdown = ms => {
    if (ms <= 0) return '0s';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);
    return parts.join(' ');
};

const BuyPacksPage = () => {
    const [rotation, setRotation] = useState(null);
    const [allSeries, setAllSeries] = useState([]);
    const [wallet, setWallet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [countdown, setCountdown] = useState('');
    const [packCount, setPackCount] = useAtom(packCountAtom);
    const [currentUser] = useAtom(currentUserAtom);
    const isAdmin = currentUser?.isAdmin;
    const [previewPack, setPreviewPack] = useState(null);

    const refreshPackCount = async () => {
        try {
            const userPacks = await fetchUserPacks();
            const total = userPacks.reduce((sum, p) => sum + p.amount, 0);
            setPackCount(total);
        } catch (_) {
            // silently ignore
        }
    };

    const loadRotation = useCallback(async () => {
        try {
            const data = await fetchStoreRotation();
            setRotation(data);
        } catch (err) {
            console.error('Error fetching store rotation:', err);
            setError('Error fetching store rotation');
            toast.error('Error fetching store rotation');
        }
    }, []);

    useEffect(() => {
        const fetchInfo = async () => {
            setLoading(true);
            setError(null);
            try {
                const fetches = [fetchWallet(), loadRotation()];
                if (currentUser?.isAdmin) {
                    fetches.push(
                        fetchAllSeries().then(series => {
                            const active = series.filter(s => s.isActive);
                            setAllSeries(active);
                        })
                    );
                }
                const [walletData] = await Promise.all(fetches);
                setWallet(walletData.balance);
            } catch (err) {
                setError('Error fetching store data');
                toast.error('Error fetching store data: ' + err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchInfo();
    }, [loadRotation, currentUser?.isAdmin]);

    // Countdown timer
    useEffect(() => {
        if (!rotation?.endTime) return;

        const tick = () => {
            const remaining = new Date(rotation.endTime).getTime() - Date.now();
            if (remaining <= 0) {
                setCountdown('0s');
                loadRotation();
                return;
            }
            setCountdown(formatCountdown(remaining));
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [rotation?.endTime, loadRotation]);

    const handleBuyPack = async (pack, quantity) => {
        try {
            await buyPack(pack.seriesID, quantity);
            setWallet(prev => prev - quantity * (pack.cost ?? 0));
            toast.success(`${quantity} pack(s) purchased successfully!`);
            await refreshPackCount();
        } catch (err) {
            setError('Error purchasing pack');
            toast.error('Error purchasing pack: ' + err.message);
        }
    };

    const packs = useMemo(
        () => [...(rotation?.packs ?? [])].sort((a, b) => (b.year ?? 0) - (a.year ?? 0)),
        [rotation?.packs]
    );
    const sortedAllSeries = useMemo(() => [...allSeries].sort((a, b) => (b.year ?? 0) - (a.year ?? 0)), [allSeries]);

    return (
        <div className="buy-packs-page">
            <div className="header-info">
                <h2>
                    <Link to="/packs/user" className="button">
                        My Packs <PackBadge count={packCount} />
                    </Link>{' '}
                    / Buy Packs
                </h2>
                <h2>${wallet}</h2>
            </div>
            {error && <p className="error">{error}</p>}
            {loading ? (
                <LoadingSpinner />
            ) : packs.length === 0 ? (
                <p>No packs available right now.</p>
            ) : (
                <>
                    <div className="packs-grid">
                        {packs.map(pack => (
                            <div key={pack.seriesID} className="pack-item">
                                <span className="pack-timer">{countdown}</span>
                                <img
                                    src={getCardImageSrc(pack.seriesKey)}
                                    alt={`${pack.name} pack art`}
                                    className="pack-image"
                                    onError={handleCardImageError}
                                />
                                <div className="pack-details">
                                    <span className="pack-title">
                                        {pack.name} ({pack.year})
                                        <span className="pack-icons">
                                            <VariationRatesTooltip seriesID={pack.seriesID} />
                                            <button
                                                className="icon-button"
                                                onClick={() => setPreviewPack(pack)}
                                                aria-label="View cards in this series"
                                            >
                                                <FontAwesomeIcon icon={faListUl} />
                                            </button>
                                        </span>
                                    </span>
                                    <span className="pack-price"> (${pack.cost}) </span>
                                    <div className="pack-buttons">
                                        {[1, 10, 25, 50, 100].map(qty => (
                                            <button
                                                key={qty}
                                                onClick={() => handleBuyPack(pack, qty)}
                                                disabled={wallet < qty * (pack.cost ?? 0)}
                                            >
                                                Buy {qty}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {isAdmin && sortedAllSeries.length > 0 && (
                        <>
                            <h3>All Packs (Admin)</h3>
                            <div className="packs-grid">
                                {sortedAllSeries.map(pack => (
                                    <div key={pack.seriesID} className="pack-item">
                                        <img
                                            src={getCardImageSrc(pack.seriesKey)}
                                            alt={`${pack.name} pack art`}
                                            className="pack-image"
                                            onError={handleCardImageError}
                                        />
                                        <div className="pack-details">
                                            <span className="pack-title">
                                                {pack.name} {pack.year ? `(${pack.year})` : ''}
                                                <span className="pack-icons">
                                                    <VariationRatesTooltip seriesID={pack.seriesID} />
                                                    <button
                                                        className="icon-button"
                                                        onClick={() => setPreviewPack(pack)}
                                                        aria-label="View cards in this series"
                                                    >
                                                        <FontAwesomeIcon icon={faListUl} />
                                                    </button>
                                                </span>
                                            </span>
                                            <span className="pack-price">
                                                {' '}
                                                {pack.cost ? `($${pack.cost})` : '(Free)'}{' '}
                                            </span>
                                            <div className="pack-buttons">
                                                {[1, 10, 25, 50, 100].map(qty => (
                                                    <button
                                                        key={qty}
                                                        onClick={() => handleBuyPack(pack, qty)}
                                                        disabled={wallet < qty * (pack.cost ?? 0)}
                                                    >
                                                        Buy {qty}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}
            {previewPack && (
                <SeriesCardsModal
                    seriesID={previewPack.seriesID}
                    year={previewPack.year}
                    seriesName={previewPack.name}
                    onClose={() => setPreviewPack(null)}
                />
            )}
        </div>
    );
};

export default BuyPacksPage;
